import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@shared/api/client";

export interface CompraOcrCampos {
  proveedor_nombre: string | null;
  proveedor_cif: string | null;
  numero_factura: string | null;
  fecha: string | null;
  vencimiento: string | null;
  base: number | null;
  iva_importe: number | null;
  total: number | null;
}

export interface ScanResult {
  ok: boolean;
  archivo: string;
  archivo_disk: string;
  ocr: { ok: boolean; campos?: CompraOcrCampos; error?: string };
}

export interface FacturaRecibida {
  id: number;
  numero_factura: string | null;
  fecha: string | null;
  vencimiento: string | null;
  base: number;
  iva_importe: number;
  total: number;
  estado_pago: "pendiente" | "pagada";
  categoria: string | null;
  archivo: string | null;
  notas: string | null;
  proveedor?: { id: number; nombre: string; cif: string | null };
  emisor?: { id: number; nombre: string } | null;
}

export interface CompraPayload {
  proveedor_nombre: string;
  proveedor_cif?: string | null;
  emisor_id?: number | null;
  numero_factura?: string | null;
  fecha?: string | null;
  vencimiento?: string | null;
  base: number;
  iva_importe: number;
  total: number;
  categoria?: string | null;
  archivo?: string | null;
  archivo_disk?: string | null;
  datos_ocr?: Record<string, unknown> | null;
  notas?: string | null;
}

const BASE = "/purchases";
const key = ["purchases"] as const;

export function useCompras(estado?: string) {
  return useQuery({
    queryKey: [...key, estado ?? "all"],
    queryFn: async () =>
      (await api.get<{ data: FacturaRecibida[]; pendiente_total: number }>(BASE, {
        params: { estado_pago: estado || undefined },
      })).data,
  });
}

export function useScanCompra() {
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("archivo", file);
      return (await api.post<ScanResult>(`${BASE}/scan`, fd)).data;
    },
  });
}

export function useCreateCompra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CompraPayload) =>
      (await api.post(`${BASE}`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export function useUpdateCompra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<CompraPayload> & { estado_pago?: string } }) =>
      (await api.patch(`${BASE}/${id}`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export function useDeleteCompra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => (await api.delete(`${BASE}/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export async function downloadCompra(id: number) {
  const res = await api.get(`${BASE}/${id}/download`, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  window.open(url, "_blank");
}
